import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { X, Star, Check } from "lucide-react";

// Avaliação opcional da conversa: 1–5 estrelas + comentário + consentimento
// LGPD pra virar depoimento anônimo no site (nada é publicado sem o Antonio aprovar).
export function FeedbackModal({ open, onClose, conversationId, onRated }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setStars(0); setHover(0); setComment(""); setConsent(false);
      setBusy(false); setDone(false); setErr(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("/feedback", {
        conversation_id: conversationId || null,
        stars,
        comment,
        consent_publico: consent,
      });
      setDone(true);
      onRated?.();
      setTimeout(onClose, 1400);
    } catch (e) {
      setErr(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        data-testid="feedback-modal"
        role="dialog"
        aria-modal="true"
        className="w-full sm:max-w-md bg-popover border border-border rounded-t-3xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-bold tracking-tight">Avaliar conversa</h2>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted" data-testid="feedback-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-2">
            <div className="inline-flex rounded-full bg-emerald-500/10 p-3">
              <Check className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="font-semibold">Valeu pelo feedback! 💜</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">Como foi essa conversa com o tutor?</p>
              <div className="flex gap-1.5" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    data-testid={`feedback-star-${s}`}
                    onClick={() => setStars(s)}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${s} estrela${s > 1 ? "s" : ""}`}
                  >
                    <Star
                      className={`h-7 w-7 transition-colors ${
                        s <= (hover || stars) ? "text-amber-400 fill-amber-400" : "text-border"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <textarea
              data-testid="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Conta o que curtiu ou o que dava pra melhorar (opcional)"
              className="w-full rounded-lg bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 resize-none scrollbar-thin"
            />

            <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                data-testid="feedback-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded accent-violet-600"
              />
              <span>
                Autorizo usar meu comentário <strong className="text-foreground/80">de forma anônima</strong> como
                depoimento no site. (Opcional — seu nome nunca aparece.)
              </span>
            </label>

            {err && <p className="text-xs text-red-400">{err}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={onClose}
                className="rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                Agora não
              </button>
              <button
                data-testid="feedback-submit-btn"
                onClick={submit}
                disabled={busy || stars === 0}
                className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-5 py-2 text-sm font-medium disabled:opacity-50 transition"
              >
                {busy ? "Enviando..." : "Enviar avaliação"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
